"use client";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import {
  DefaultizedPieValueType,
  legendClasses,
  PieChart as MUIPieChart,
  pieArcLabelClasses,
  PieValueType,
} from "@mui/x-charts";
import { useState } from "react";

export type PieChartData = {
  id: number;
  value: number;
  label: string;
  color: "green" | "red" | "orange" | "lightGray" | "gray" | "secondary" | "blue";
};

type PieChartProps = {
  data: PieChartData[];
  width?: number;
  height?: number;
  showLabels?: boolean;
  innerRadius?: number;
  showAsPercentage?: boolean;
  isLoading?: boolean;
  legendOutsideChart?: boolean;
  valueFormatter?: (value: PieValueType) => string;
  labelFontWeight?: number;
  labelFillColor?: string;
};

type ArcLabelItem = Omit<DefaultizedPieValueType, "label"> & { label?: string };

export const PieChart = ({
  data,
  legendOutsideChart,
  width = 320,
  height = 160,
  showLabels = true,
  innerRadius = 40,
  showAsPercentage = true,
  isLoading,
  valueFormatter,
  labelFontWeight = 400,
  labelFillColor = "white",
}: PieChartProps) => {
  const theme = useTheme();

  const colorMap: { [key in PieChartData["color"]]: string } = {
    green: theme.palette.success[500] || "",
    red: theme.palette.error[500] || "",
    orange: theme.palette.warning[500] || "",
    lightGray: theme.palette.neutral[100] || "",
    gray: theme.palette.neutral[300] || "",
    secondary: theme.palette.secondary.main || "",
    blue: theme.palette.info[500] || "",
  };

  const totalValue = data.reduce((acc, item) => acc + item.value, 0);

  const getArcLabel = (item: ArcLabelItem) =>
    showAsPercentage ? `${((item.value / totalValue) * 100).toFixed(0)}%` : `${item.value}`;

  // do not show value 0 in pie chart
  const nonZeroData = data.filter((entry) => entry.value > 0);
  const [date] = useState(new Date().toLocaleString());

  return (
    <Grid container alignContent="center">
      <Grid item xs={legendOutsideChart ? 7 : 12} justifyItems="center">
        <MUIPieChart
          loading={isLoading}
          series={[
            {
              data: nonZeroData.map((entry) => ({
                ...entry,
                color: colorMap[entry.color],
              })),
              arcLabel: showLabels ? getArcLabel : undefined,
              innerRadius: innerRadius,
              valueFormatter: valueFormatter ? valueFormatter : (val) => `${val.value}`,
              cx: legendOutsideChart ? "80%" : "50%",
              arcLabelRadius: legendOutsideChart ? "125%" : "75%",
            },
          ]}
          sx={{
            [`& .${pieArcLabelClasses.root}`]: {
              fill: labelFillColor,
              fontWeight: labelFontWeight,
            },
            [`& .${legendClasses.mark}`]: {
              rx: "10",
              ry: "10",
            },
          }}
          width={width}
          height={height}
          slotProps={{ legend: { hidden: legendOutsideChart ? true : false } }}
        />
      </Grid>
      {legendOutsideChart && (
        <Grid item xs={5} alignContent="center">
          <Typography mb={5}>{date}</Typography>
          {data.map((item) => (
            <Box key={item.id} display="flex" alignItems="center" my={1}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: item.color,
                  marginRight: 1,
                }}
              />
              <Typography>{item.label}</Typography>
            </Box>
          ))}
        </Grid>
      )}
    </Grid>
  );
};
